import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

import { FormattedMessage } from '../../util/reactIntl';

type ActiveBoost = {
  id: string;
  duration: string;
  expiresAt: string;
};

/**
 * Format remaining ms as a compact countdown:
 *   ≥ 1 day  → "{d}d {h}h"
 *   ≥ 1 hour → "{h}h {m}m"
 *   else     → "{m}m"
 */
const formatRemaining = (ms: number): { id: string; values: Record<string, number> } => {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return { id: 'BoostStatusPill.dh', values: { d: days, h: hours } };
  if (hours > 0) return { id: 'BoostStatusPill.hm', values: { h: hours, m: mins } };
  return { id: 'BoostStatusPill.m', values: { m: mins } };
};

type Props = {
  activeBoost: ActiveBoost;
  className?: string;
  size?: 'sm' | 'md';
};

const BoostStatusPill = ({ activeBoost, className = '', size = 'md' }: Props) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const ms = new Date(activeBoost.expiresAt).getTime() - Date.now();
  const remaining = formatRemaining(ms);
  const isSmall = size === 'sm';

  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-md border border-primary/50 bg-white font-semibold uppercase tracking-[0.14em] text-[#171513]',
        isSmall ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1 text-[11px]',
        className,
      ].join(' ')}
      data-testid={`pill-boost-active-${activeBoost.id}`}
    >
      <Sparkles className={`text-primary ${isSmall ? 'h-3 w-3' : 'h-3.5 w-3.5'}`} />
      <FormattedMessage id="BoostStatusPill.label" />
      <span aria-hidden="true">·</span>
      <FormattedMessage id={remaining.id} values={remaining.values} />
    </span>
  );
};

export default BoostStatusPill;
